import { Link } from 'react-router-dom';
import type { EnvironmentHealthView } from '../../api';
import { Callout } from '../primitives';
import { Ago } from '../status/ago';
import { actionLabel } from '../operation/actionLabels';
import type { SectionPath } from './operationalHealth';
import { buildStatusStrip, type ReadinessSummary, type StripItem, type StripPart } from './statusStrip';

/**
 * StatusBand: the project Overview's four facts in one compact strip —
 * Site, Last deploy, Infrastructure, Backup — each a status word and a few
 * short metrics ("6/6 checks · 1m ago"), never a sentence. Normal is quiet:
 * a healthy item is plain text with a small dot; a warning or failure gets
 * a tinted edge, so a problem is the loudest thing on the page. Each item
 * links to where the depth is; its accessible name carries the full
 * sentence the visible text leaves out.
 */

function Parts({ parts }: { parts: StripPart[] }) {
  return (
    <>
      {parts.map((part, index) => (
        <span key={index}>
          {index > 0 ? ' · ' : null}
          {'text' in part ? part.text : <Ago iso={part.ago} />}
        </span>
      ))}
    </>
  );
}

function Item({ item }: { item: StripItem }) {
  return (
    <li className="status-strip__item" data-tone={item.tone} data-testid={`band-${item.key === 'deploy' ? 'deployment' : item.key}`}>
      <Link className="status-strip__link" to={item.to} aria-label={item.spoken}>
        <span className="status-strip__label">{item.label}</span>
        <span className={`status-strip__value status-strip__value--${item.tone}`}>
          <span className="status-strip__dot" aria-hidden="true" />
          {'text' in item.value ? item.value.text : <Ago iso={item.value.ago} />}
        </span>
        {item.parts.length > 0 ? (
          <span className="status-strip__meta">
            <Parts parts={item.parts} />
          </span>
        ) : null}
        {item.detail && item.detail.length > 0 ? (
          <span className="status-strip__detail">
            <Parts parts={item.detail} />
          </span>
        ) : null}
      </Link>
    </li>
  );
}

export function StatusBand({
  health,
  loading,
  error,
  onRetry,
  sectionPath,
  readiness,
  deploymentsElsewhere = false,
}: {
  health: EnvironmentHealthView | null | undefined;
  loading: boolean;
  error: Error | null;
  onRetry?: () => void;
  sectionPath: SectionPath;
  /** The readiness ledger's counts, shown beside the services. */
  readiness?: ReadinessSummary | null;
  /** An SSH environment: its deployments are on its environment page. */
  deploymentsElsewhere?: boolean;
}) {
  if (loading && !health) {
    return (
      <ul className="status-strip status-strip--loading" aria-busy="true" aria-label="Reading this environment" data-testid="status-band">
        {['Site', 'Last deploy', 'Infrastructure', 'Backup'].map((label) => (
          <li key={label} className="status-strip__item">
            <span className="status-strip__label">{label}</span>
            <span className="skeleton status-strip__skeleton" />
          </li>
        ))}
      </ul>
    );
  }
  if (error && !health) {
    return (
      <Callout
        tone="warning"
        title="Status unavailable"
        testId="status-band-error"
        actions={
          onRetry ? (
            <button type="button" className="button button--secondary button--small" onClick={onRetry}>
              {actionLabel.retry}
            </button>
          ) : undefined
        }
      >
        {error.message}
      </Callout>
    );
  }
  const items = buildStatusStrip(health, { sectionPath, readiness, deploymentsElsewhere });
  return (
    <ul className="status-strip" aria-label="Status" data-testid="status-band">
      {items.map((item) => (
        <Item key={item.key} item={item} />
      ))}
    </ul>
  );
}
